import { useEffect, useRef, useState, useCallback } from 'react';
import { BACKEND_URL } from '@/lib/backendUrl';

/**
 * useWebSocket — Auto-reconnecting WebSocket hook
 * @param {string} path  - WS path relative to backend (e.g., "/api/ws/watcher")
 * @param {function} onMessage - callback for each parsed JSON message
 * @returns {{ connected, clientCount, send }}
 */
export function useWebSocket(path, onMessage) {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const getWsUrl = useCallback(() => {
    const backendUrl = BACKEND_URL || '';
    // Convert http(s) to ws(s)
    const wsBase = backendUrl.replace(/^http/, 'ws');
    return `${wsBase}${path}`;
  }, [path]);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const ws = new WebSocket(getWsUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        if (reconnectTimer.current) {
          clearTimeout(reconnectTimer.current);
          reconnectTimer.current = null;
        }
      };

      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          onMessageRef.current?.(data);
        } catch {}
      };

      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;
        // Auto-reconnect after 3s
        reconnectTimer.current = setTimeout(connect, 3000);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch {
      // WS not available, retry
      reconnectTimer.current = setTimeout(connect, 5000);
    }
  }, [getWsUrl]);

  useEffect(() => {
    connect();
    // Ping keepalive every 25s
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send('ping');
      }
    }, 25000);
    return () => {
      clearInterval(pingInterval);
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [connect]);

  const send = useCallback((data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(typeof data === 'string' ? data : JSON.stringify(data));
    }
  }, []);

  return { connected, send };
}
